import { IModuleInjection } from "app/di/DependencyInjections";
import { ActionLogInjection } from "features/actionLogManagement/di/ActionLogInjection";
import { AirtimeManagementInjection } from "features/airTimeManagement/di/AirtimeManagementInjection";
import { AirtimeRegistrationInjection } from "features/airTimeRegistration/di/AirTimeRegistrationInjection";
import { ApiDocumentInjection } from "features/apiDocumentFeature/di/ApiDocumentInjection";
import { AppFeedbackInjection } from "features/appFeedback/di/AppFeedbackInjection";
import { AppProfileInjection } from "features/appProfileRegister/di/AppProfieInjection";
import { BalanceHistoryInjection } from "features/balanceHistory/di/BalanceHistoryInjection";
import { CapabilityInjection } from "features/capabilityManagement/di/CapabilityInjection";
import { CashFlowInjection } from "features/cashFlow/di/CashFlowInjection";
import { CdnConfigInjection } from "features/cdnConfiguration/di/CdnConfigInjection";
import { CountryConfigInjection } from "features/countryConfiguration/di/CountryConfigInjection";
import { DiscountManagementInjection } from "features/discountManagement/di/DiscountManagementInjection";
import { ESimplifiedBuyingInjection } from "features/eSimplifiedBuyingBeta/di/ESimplifiedBuyingInjection";
import { ErrorCodeGeneralInjection } from "features/errorCodeGeneralFeature/di/ErrorCodeGeneralInjection";
import { GeneralsInjection } from "features/generals/di/GeneralsInjection";
import { IndividualUserInjection } from "features/individualUsersManagement/di/IndividualUserInjection";
import { IOTSimRegistrationInjection } from "features/iotSimRegistration/di/IOTSimRegistrationInjection";
import { MobiSimRegistrationInjection } from "features/mobiSimRegistration/di/MobiSimRegistrationInjection";
import { NiceSimRegistrationInjection } from "features/niceSimRegistration/di/NiceSimRegistrationInjection";
import Notfound from "features/notFound/NotFound";
import { OfficialDispatchInjection } from "features/officialDispatch/di/OfficialDispatchInjection";
import { PackageLookupInjection } from "features/packageLookup/di/PackageLookupInjection";
import { PackageRegistrationInjection } from "features/packageRegistration/di/PackageRegistrationInjection";
import { PermissionDataInjection } from "features/permissionDataManagement/di/PermissionDataInjection";
import { RoleManagementInjection } from "features/roleManagement/di/RoleManagementInjection";
import { SimLookupInjection } from "features/simStatusLookup/di/SimLookupInjection";
import { SupplierManagementInjection } from "features/supplierManagement/di/SupplierManagmentInjection";
import { SystemUserInjection } from "features/systemUserManagement/di/SystemUserInjection";
import { UserPermissionInjection } from "features/userPermissionManagement/di/UserPermissionInjection";
import { ComponentType, FC, lazy, LazyExoticComponent } from "react";
import { MobileDataService } from "utils/enums/MobileDataEnums";
import { TypeOfSupportEnum } from "utils/enums/TypeOfSupportEnum";

export interface IAppPages {
  Component: LazyExoticComponent<FC<any> | ComponentType>;
  token?: symbol | string;
  injectionModule?: IModuleInjection;
  addtionalProps?: unknown;
}

class AppPages {
  private static readonly B2BPackageManagementPage: IAppPages = {
    Component: lazy(
      () =>
        import("features/packageManagement/b2bPackageManagement/presentation/view/B2BPackageManagementFeature"),
    ),
    addtionalProps: {
      service: MobileDataService.BuyPackage,
      categoriesForQuery: "",
    },
  };

  static PageKeys: Record<string, IAppPages> = {
    GeneralFeature: {
      Component: lazy(
        () => import("features/generals/presentation/views/GeneralFeature"),
      ),
      injectionModule: GeneralsInjection,
    },
    ErrorCodeGeneralFeature: {
      Component: lazy(
        () =>
          import("features/errorCodeGeneralFeature/presentation/views/ErrorCodeGeneralFeature"),
      ),
      injectionModule: ErrorCodeGeneralInjection,
    },
    AppSimMobifoneFeature: {
      Component: lazy(
        () =>
          import("features/mobiSimRegistration/presentation/views/MobiSimRegistrationFeature"),
      ),
      injectionModule: MobiSimRegistrationInjection,
    },
    IOTSimRegistrationFeature: {
      Component: lazy(
        () =>
          import("features/iotSimRegistration/presentation/views/IOTSimRegistrationFeature"),
      ),
      injectionModule: IOTSimRegistrationInjection,
    },
    AirTimeManagementFeature: {
      Component: lazy(
        () =>
          import("features/airTimeManagement/presentation/views/AirTimeManagementFeature"),
      ),
      injectionModule: AirtimeManagementInjection,
    },
    AirTimeRegistrationFeature: {
      Component: lazy(
        () =>
          import("features/airTimeRegistration/presentation/views/AirTimeRegistrationFeature"),
      ),
      injectionModule: AirtimeRegistrationInjection,
    },
    AppDataRegisterFeature: {
      Component: lazy(
        () =>
          import("features/packageRegistration/presentation/views/PackageRegistrationFeature"),
      ),
      injectionModule: PackageRegistrationInjection,
    },
    AppNiceSimFeature: {
      Component: lazy(
        () =>
          import("features/niceSimRegistration/presentation/views/NiceSimRegistrationFeature"),
      ),
      injectionModule: NiceSimRegistrationInjection,
    },
    BalanceHistoryFeature: {
      Component: lazy(
        () =>
          import("features/balanceHistory/presentation/view/BalanceHistoryFeature"),
      ),
      injectionModule: BalanceHistoryInjection,
    },
    IoTSupportManagementFeature: {
      Component: lazy(
        () =>
          import("features/iotSupportManagement/IoTSupportManagementFeature"),
      ),
    },
    TravelSimRegistrationManagementFeature: {
      Component: lazy(
        () =>
          import("features/travelSimRegistration/presentation/TravelSimRegistrationManagementFeature"),
      ),
    },
    CashFlowFeature: {
      Component: lazy(
        () => import("features/cashFlow/presentation/views/CashFlowFeature"),
      ),
      injectionModule: CashFlowInjection,
    },
    PackageLookupFeature: {
      Component: lazy(
        () =>
          import("features/packageLookup/presentation/view/PackageLookupFeature"),
      ),
      injectionModule: PackageLookupInjection,
    },
    SimLookupFeature: {
      Component: lazy(
        () =>
          import("features/simStatusLookup/presentation/view/SimLookupFeature"),
      ),
      injectionModule: SimLookupInjection,
    },
    ApiDocManagementFeature: {
      Component: lazy(
        () =>
          import("features/apiDocumentFeature/presentation/view/ApiDocumentFeature"),
      ),
      injectionModule: ApiDocumentInjection,
      addtionalProps: TypeOfSupportEnum.apiDoc,
    },
    DispatchManagementFeature: {
      Component: lazy(
        () =>
          import("features/officialDispatch/presentation/view/OfficialDispatchFeature"),
      ),
      injectionModule: OfficialDispatchInjection,
      addtionalProps: TypeOfSupportEnum.officialDispatch,
    },
    PackageManagementFeature: this.B2BPackageManagementPage,
    B2BPackageManagementFeature: this.B2BPackageManagementPage,
    DiscountManagementFeature: {
      Component: lazy(
        () =>
          import("features/discountManagement/presentation/view/DiscountManagementView"),
      ),
      injectionModule: DiscountManagementInjection,
    },
    AppProfileRegisterFeature: {
      Component: lazy(
        () =>
          import("features/appProfileRegister/presentation/view/AppProfileRegisterView"),
      ),
      injectionModule: AppProfileInjection,
    },
    IndividualUsersManagementFeature: {
      Component: lazy(
        () =>
          import("features/individualUsersManagement/presentation/views/IndividualUsersManagementView"),
      ),
      injectionModule: IndividualUserInjection,
    },
    AppFeedbackFeature: {
      Component: lazy(
        () =>
          import("features/appFeedback/presentation/views/AppFeedbackFeature"),
      ),
      injectionModule: AppFeedbackInjection,
    },
    SystemUsersManagementFeature: {
      Component: lazy(
        () =>
          import("features/systemUserManagement/presentation/view/SystemUserView"),
      ),
      injectionModule: SystemUserInjection,
    },
    B2CUserManagementFeature: {
      Component: lazy(
        () =>
          import("features/b2cUserManagement/presentation/views/B2CUserManagementFeature"),
      ),
    },
    PartnerManagementFeature: {
      Component: lazy(
        () =>
          import("features/partnerManagement/presentation/PartnerManagementFeature"),
      ),
    },
    SupplierManagementFeature: {
      Component: lazy(
        () =>
          import("features/supplierManagement/presentation/views/SupplierManagementFeature"),
      ),
      injectionModule: SupplierManagementInjection,
    },
    ProvinceConfigurationFeature: {
      Component: lazy(
        () =>
          import("features/provinceConfiguration/views/ProvinceConfigurationFeature"),
      ),
    },
    WardConfigurationFeature: {
      Component: lazy(
        () =>
          import("features/wardConfiguration/views/WardConfigurationFeature"),
      ),
    },
    DistrictConfigurationFeature: {
      Component: lazy(
        () =>
          import("features/districtConfiguration/views/DistrictConfigurationFeature"),
      ),
    },
    CountryConfigurationFeature: {
      Component: lazy(
        () =>
          import("features/countryConfiguration/presentation/views/CountryConfigurationFeature"),
      ),
      injectionModule: CountryConfigInjection,
    },
    CdnConfigurationFeature: {
      Component: lazy(
        () =>
          import("features/cdnConfiguration/presentation/view/CdnConfigurationView"),
      ),
      injectionModule: CdnConfigInjection,
    },
    ManageConfigFeature: {
      Component: lazy(
        () => import("features/manageConfig/views/ManageConfigFeature"),
      ),
    },
    PermissionDataFeature: {
      Component: lazy(
        () =>
          import("features/permissionDataManagement/presentation/views/PermissionDataFeature"),
      ),
      injectionModule: PermissionDataInjection,
    },
    CapabilityFeature: {
      Component: lazy(
        () =>
          import("features/capabilityManagement/presentation/views/CapabilityFeature"),
      ),
      injectionModule: CapabilityInjection,
    },
    UserPermissionFeature: {
      Component: lazy(
        () =>
          import("features/userPermissionManagement/presentation/views/UserPermissionFeature"),
      ),
      injectionModule: UserPermissionInjection,
    },
    RoleManagementFeature: {
      Component: lazy(
        () =>
          import("features/roleManagement/presentation/views/RoleManagementFeature"),
      ),
      injectionModule: RoleManagementInjection,
    },
    ActionLogFeature: {
      Component: lazy(
        () =>
          import("features/actionLogManagement/presentation/views/ActionLogFeature"),
      ),
      injectionModule: ActionLogInjection,
    },
    ESimplifiedBuyingBetaFeature: {
      Component: lazy(
        () =>
          import("features/eSimplifiedBuyingBeta/presentation/view/ESimplifiedBuyingView"),
      ),
      injectionModule: ESimplifiedBuyingInjection,
    },
  };

  static getPage(key: string): any {
    return this.PageKeys[key]?.Component || Notfound;
  }

  static getToken(key: string) {
    return this.PageKeys[key]?.token;
  }

  static getAddtionalProps(key: string) {
    return this.PageKeys[key]?.addtionalProps;
  }

  static getInjectionModule(key: string) {
    return this.PageKeys[key]?.injectionModule;
  }
}

export default AppPages;
